import React, { useState, useEffect } from "react";
import { FlatList, Image, Keyboard, Text, View } from "react-native";
import { Picker } from "@react-native-picker/picker";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import Separator from "../Separator";
import CommonTextInputWeb from "../../common/CommonTextInputWeb";
import UploadFileComponent from "../ongoing-common-components/result-compnents/UploadFileComponent";
import { TouchableOpacity } from "react-native";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import { updateUserProfile } from "../../services/authAPI";
import { toast } from "react-toastify";
import { handleApiError } from "../../services/errorHandler";
import { searchUsers } from "../../services/eventsAPI";
import { countryWithTimezone } from "../../Api/configs";
import AccountTabSkeleton from "../skeleton-components/AccountTabSkeleton";
import { useUserProfile } from "../../hooks/useUserProfile";

const AccountTabComponent = ({ accessToken }) => {
  const { userProfileData, loading: profileLoading } = useUserProfile(accessToken);
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [isLoading, setIsLoading] = useState(false);
  const [updates, setUpdates] = useState({});
  const [accountDetails, setAccountDetails] = useState({
    email: "",
    bio: "",
    username: "",
    firstName: "",
    lastName: "",
    userLanguage: "",
    userTimeZone: "",
    userCountry: "",
    emailPrivateMessage: false,
    emailBattleNotification: false,
    readOnChallenge: false,
    occasionalEmail: false,
    blockedUsers: [],
  });
  const [searchText, setSearchText] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (userProfileData) {
      setAccountDetails({
        email: userProfileData.email || "",
        bio: userProfileData.bio || "",
        username: userProfileData.userID || "",
        firstName: userProfileData.firstName || "",
        lastName: userProfileData.lastName || "",
        userLanguage: userProfileData.language || "",
        userTimeZone: userProfileData.timeZone || "",
        userCountry: userProfileData.country || "",
        emailPrivateMessage: userProfileData.emailPrivateMessage || false,
        emailBattleNotification: userProfileData.emailBattleNotification || false,
        readOnChallenge: userProfileData.readOnChallenge || false,
        occasionalEmail: userProfileData.occasionalEmail || false,
        blockedUsers: userProfileData.blockedUsers || [],
      });
      setBlockedUsers(userProfileData.blockedUsers || []);
      setSelectedCountry(userProfileData.country || "");
    }
  }, [userProfileData]);

  const handleCountryChange = (selectedCountry) => {
    setSelectedCountry(selectedCountry);
    const timezoneData = countryWithTimezone.find(
      (item) => item.country === selectedCountry
    );
    if (timezoneData) {
      setSelectedTimezone(timezoneData.timezone);
      handleInputChange("userTimeZone", timezoneData.timezone);
      handleInputChange("userCountry", selectedCountry);
    } else {
      toast.error("Timezone not found for the selected country.");
    }
  };

  const handleSearchChange = async (text) => {
    setSearchText(text);
    if (text.length > 0) {
      const query = text;
      await callSearchAPI({
        bodyParams: { query },
        responseSuccess: (response) => {
          setFilteredSuggestions(response.results);
          setShowSuggestions(true);
        },
      });
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    if (!blockedUsers.includes(suggestion)) {
      const updatedBlockedUsers = [...blockedUsers, suggestion];
      setBlockedUsers(updatedBlockedUsers);
      handleInputChange("blockedUsers", updatedBlockedUsers);
    }
    setSearchText(suggestion);
    setShowSuggestions(false);
    Keyboard.dismiss();
  };

  const handleLearnMorePress = () => {};

  const handleInputChange = (name, value) => {
    setAccountDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
    setUpdates((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const callUpdateEventAPI = async () => {
    const bodyParams = {
      accessToken: accessToken,
      username: userProfileData.userID,
      ...updates,
    };
    try {
      setShowLoader(true);
      const response = await updateUserProfile(bodyParams);
      if (response.statusCode === 200 || response.statusCode === 201) {
        setShowLoader(false);
        if (response.body) {
          toast.success(response.body.message);
        }
      } else if (
        response.statusCode === 400 &&
        response.body.error === "No valid fields to update"
      ) {
        setShowLoader(false);
        toast.error("No changes to update");
      } else {
        setShowLoader(false);
        handleApiError(response.statusCode, response.body?.error || "");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  const callSearchAPI = async ({ bodyParams, responseSuccess }) => {
    try {
      const data = await searchUsers(bodyParams);
      if (data.statusCode === 200) {
        responseSuccess(data.body);
      } else {
        toast.error(data.body.error || "An error occurred");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderCustomCheckBox = (isChecked, onPress) => (
    <TouchableOpacity
      onPress={onPress}
      style={{
        width: 17,
        height: 17,
        borderRadius: 12,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: isChecked
          ? AppColors.appViolentColor_6D08C3
          : AppColors.white,
      }}
    >
      {isChecked && (
        <View
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: AppColors.appViolentColor_6D08C3,
          }}
        />
      )}
    </TouchableOpacity>
  );

  // Conditionally render skeleton or actual content
  if (profileLoading) {
    return <AccountTabSkeleton />;
  }

  return (
    <View
      style={{
        backgroundColor: AppColors.purple_453B50,
        borderRadius: 10,
        padding: 20,
        opacity: showLoader ? 0.5 : 1,
      }}
    >
      {/* {showLoader && <ShowLoader showIndicater={showLoader} />} */}
      <View
        style={{
          paddingTop:
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? 0
              : 30,
          paddingBottom:
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? 20
              : 50,
        }}
      >
        <Text style={[AppFontStyle.BOLD_ANYBODY_20, {}]}>Your Account</Text>
        <Separator
          color={AppColors.green_4CD964}
          height={2}
          marginTop={5}
          marginBottom={0}
          width={"40"}
          marginHorizontal={0}
        />
      </View>
      <View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Email
          </Text>
          <CommonTextInputWeb
            placeholder="milad_mn@hotmail.com"
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[
              AppFontStyle.REGULAR_ANYBODY_14,
              {
                opacity: 0.5,
              },
            ]}
            prefixIconVisibility={false}
            isPassword={false}
            value={accountDetails.email}
            editable={false}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Username
          </Text>
          <CommonTextInputWeb
            placeholder="mi"
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[
              AppFontStyle.REGULAR_ANYBODY_14,
              {
                opacity: 0.5,
              },
            ]}
            prefixIconVisibility={false}
            isPassword={false}
            value={accountDetails.username}
            editable={false}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            First Name
          </Text>
          <CommonTextInputWeb
            placeholder=""
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[AppFontStyle.REGULAR_ANYBODY_14]}
            prefixIconVisibility={false}
            onChangeText={(text) => handleInputChange("firstName", text)}
            isPassword={false}
            value={accountDetails.firstName}
            onSubmitEditing={() => {
              Keyboard.dismiss();
            }}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Last Name
          </Text>
          <CommonTextInputWeb
            placeholder=""
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[AppFontStyle.REGULAR_ANYBODY_14]}
            prefixIconVisibility={false}
            onChangeText={(text) => handleInputChange("lastName", text)}
            isPassword={false}
            value={accountDetails.lastName}
            onSubmitEditing={() => {
              Keyboard.dismiss();
            }}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Bio
          </Text>
          <CommonTextInputWeb
            placeholder=""
            multiLine={true}
            mainStyle={[styles.accountTextInputStyle]}
            inputStyle={[AppFontStyle.REGULAR_ANYBODY_14]}
            prefixIconVisibility={false}
            onChangeText={(text) => handleInputChange("bio", text)}
            isPassword={false}
            value={accountDetails.bio}
            onSubmitEditing={() => {
              Keyboard.dismiss();
            }}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Language
          </Text>
          <CommonTextInputWeb
            placeholder="English"
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[AppFontStyle.REGULAR_ANYBODY_14]}
            prefixIconVisibility={false}
            onChangeText={(text) => handleInputChange("userLanguage", text)}
            isPassword={false}
            value={accountDetails.userLanguage}
            onSubmitEditing={() => {
              Keyboard.dismiss();
            }}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Country
          </Text>
          <View
            style={{
              height:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? 50
                  : "auto",
            }}
          >
            <Picker
              selectedValue={selectedCountry}
              style={[
                styles.accountInputStyle,
                AppFontStyle.REGULAR_ANYBODY_14,
              ]}
              onValueChange={(itemValue) => handleCountryChange(itemValue)}
            >
              {countryWithTimezone.map((item, index) => (
                <Picker.Item
                  style={[AppFontStyle.REGULAR_ANYBODY_14]}
                  key={index}
                  label={item.country}
                  value={item.country}
                />
              ))}
            </Picker>
          </View>
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Time Zone
          </Text>
          <CommonTextInputWeb
            placeholder="(+01:00) London"
            mainStyle={[styles.accountInputStyle]}
            inputStyle={[AppFontStyle.REGULAR_ANYBODY_14]}
            prefixIconVisibility={false}
            onChangeText={(text) => handleInputChange("userTimeZone", text)}
            isPassword={false}
            value={accountDetails.userTimeZone}
            onSubmitEditing={() => {
              Keyboard.dismiss();
            }}
            editable={false}
            autoCapitalize="none"
            maxLength={50}
          />
        </View>
        <View style={styles.accountData}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Avatar
          </Text>
          <View
            style={{
              width: "100%",
              flex:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? 1
                  : 0.4,
            }}
          >
            <UploadFileComponent
              styles={styles}
              profilePicture={userProfileData.profilePicture}
              userId={userProfileData.userID}
              handleUpdates={handleInputChange}
              showLoader={showLoader}
              setShowLoader={setShowLoader}
            />
          </View>
        </View>
        <View style={[styles.accountData, { alignItems: "flex-start" }]}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, styles.accountText]}>
            Messaging Options
          </Text>
          <View
            style={{
              width:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? "100%"
                  : 301,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 20,
                marginBottom: 20,
              }}
            >
              {renderCustomCheckBox(accountDetails.emailPrivateMessage, () =>
                handleInputChange(
                  "emailPrivateMessage",
                  !accountDetails.emailPrivateMessage
                )
              )}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_C3C3C3, marginTop: 1.5 },
                ]}
              >
                Email new private messages
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 20,
                marginBottom: 20,
              }}
            >
              {renderCustomCheckBox(
                accountDetails.emailBattleNotification,
                () =>
                  handleInputChange(
                    "emailBattleNotification",
                    !accountDetails.emailBattleNotification
                  )
              )}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_C3C3C3 },
                ]}
              >
                Email new battle notifications - recommended
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 20,
                marginBottom: 20,
              }}
            >
              {renderCustomCheckBox(accountDetails.readOnChallenge, () =>
                handleInputChange(
                  "readOnChallenge",
                  !accountDetails.readOnChallenge
                )
              )}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_C3C3C3 },
                ]}
              >
                Mark messages and notifications as read on challenge if they’re
                emailed
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 20,
                marginBottom: 20,
              }}
            >
              {renderCustomCheckBox(accountDetails.occasionalEmail, () =>
                handleInputChange(
                  "occasionalEmail",
                  !accountDetails.occasionalEmail
                )
              )}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_C3C3C3 },
                ]}
              >
                Send me occasional product updates and info about major
                challenges and tournaments.
              </Text>
            </View>
          </View>
        </View>
        <View
          style={{
            width:
              deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                ? "auto"
                : 390,
            flexDirection: "row",
            justifyContent:
              deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                ? "center"
                : "flex-end",
          }}
        >
          <CommonButton
            title="Save"
            onTap={async () => {
              await callUpdateEventAPI();
            }}
            buttonStyle={[styles.btnStyle, { paddingHorizontal: 20 }]}
            textStyle={[AppFontStyle.BOLD_ANYBODY_12, {}]}
            disabled={showLoader}
          />
        </View>
      </View>
    </View>
  );
};

export default AccountTabComponent;

const useStyles = CreateResponsiveStyle(
  {
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: AppColors.gray_6E90B4,
      borderRadius: 10,
      width: 284,
      position: "relative",
      height: 40,
    },
    searchInput: {
      flex: 1,
      color: AppColors.white,
      fontSize: 14,
    },
    accountInputStyle: {
      backgroundColor: AppColors.black,
      borderRadius: 10,
      width: 284,
      height: 40,
    },
    accountTextInputStyle: {
      backgroundColor: AppColors.black,
      borderRadius: 10,
      width: 284,
      height: 100,
    },
    accountData: {
      flexDirection: "row",
      gap: 20,
      alignItems: "center",
      marginBottom: 20,
    },
    accountText: {
      width: 87,
      textAlign: "right",
    },
    btnStyle: {
      paddingVertical: 10,
      paddingHorizontal: 10,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      borderRadius: 10,
      alignSelf: "flex-start",
      marginTop: 4,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    uploadView: {
      backgroundColor: "transparent",
      borderRadius: 0,
      paddingVertical: 0,
    },
    uploadDetails: {
      width: 284,
    },
    suggestionsList: {
      backgroundColor: AppColors.gray_6E90B4,
      position: "absolute",
      top: 45,
      zIndex: 100,
      borderRadius: 5,
      maxHeight: 150,
      width: 284,
      overflow: "hidden",
      elevation: 5,
    },
    suggestionItem: {
      padding: 10,
      borderBottomWidth: 1,
      borderBottomColor: AppColors.grayColor_D9D9D9,
    },
    suggestionText: {
      color: AppColors.black,
      fontSize: 14,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {
      searchContainer: {
        flex: 1,
        width: "100%",
      },
      suggestionsList: {
        flex: 1,
        width: "100%",
      },
      accountInputStyle: {
        flex: 1,
        width: "100%",
      },
      accountTextInputStyle: {
        flex: 1,
        width: "100%",
      },
      uploadDetails: {
        width: "100%",
      },
      accountText: {
        width: "100%",
        textAlign: "left",
      },
      accountData: {
        flexDirection: "column",
        gap: 10,
      },
    },
    [DEVICE_SIZES.XS]: {
      accountText: {
        width: "100%",
        textAlign: "left",
      },
      searchContainer: {
        flex: 1,
        width: "100%",
      },
      suggestionsList: {
        flex: 1,
        width: "100%",
      },
      accountInputStyle: {
        flex: 1,
        width: "100%",
      },
      accountTextInputStyle: {
        flex: 1,
        width: "100%",
      },
      uploadDetails: {
        width: "100%",
      },
      accountData: {
        flexDirection: "column",
        gap: 10,
      },
    },
  }
);
